import PropTypes from 'prop-types';
import React from 'react';
import { withTranslation, useTranslation, I18nextProvider } from 'react-i18next';
import cx from 'classnames';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import Shimmer from '@hsl-fi/shimmer';
import SuggestionItem from '@digitransit-component/digitransit-component-suggestion-item';
import Icon, { defaultColors } from '@digitransit-component/digitransit-component-icon';
import 'lodash/uniqWith';
import memoize from 'lodash/memoize';
import escapeRegExp from 'lodash/escapeRegExp';
import cloneDeep from 'lodash/cloneDeep';
import { createInstance } from 'i18next';

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r);
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function toPropertyKey(t) {
  var i = toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: true,
    configurable: true,
    writable: true
  }) : e[r] = t, e;
}

/**
 * Location properties.
 * @typedef {LocationProperties}
 * @property {string} layer Suggestion layer.
 * @property {lat} [number]
 * @property {lon} [number]
 * @property {string} address Address.
 */

/**
 * Returns localadmin (city name) for suggestions
 *
 * @name getLocaladmin
 * @param {Object} suggestion suggestion's properties from geocoding or a favourite stop/station.
 * Expects last part of an address (after ',') to contain the city name of the suggestion location.
 * @returns {String}  City name or empty string
 */
const getLocaladmin = suggestion => suggestion.localadmin || suggestion.localadmin || suggestion.address && suggestion.address.lastIndexOf(',') < suggestion.address.length - 2 && suggestion.address.substring(suggestion.address.lastIndexOf(',') + 2) || '';
const getStopCode = ({
  id,
  code
}) => {
  if (code) {
    return code;
  }
  if (id === undefined || typeof id.indexOf === 'undefined' || id.indexOf('#') === -1) {
    return undefined;
  }
  // id from pelias
  return id.substring(id.indexOf('#') + 1);
};
const formatFavouritePlaceLabel = (name, address) => [name || address && address.split(',')[0], typeof address === 'string' ? address.replace(new RegExp(`${escapeRegExp(name)}([ ,]|$)+`), '') : ''];

/**
 * Format suggestion-pair [name, address] by `suggestion.layer`.
 *
 * @name getNameLabel
 * @param {LocationProperties} suggestion
 * @param {boolean} [plain]
 *
 * @returns {Array.<string>} Formatted label.
 */
memoize((suggestion, plain = false) => {
  switch (suggestion.layer) {
    case 'currentPosition':
      return [suggestion.labelId, suggestion.address];
    case 'selectFromMap':
    case 'ownLocations':
    case 'back':
      return [suggestion.labelId];
    case 'favouritePlace':
      return formatFavouritePlaceLabel(suggestion.name, suggestion.address);
    case 'favouriteVehicleRentalStation':
    case 'bikestation':
      return [suggestion.name];
    case 'favouriteRoute':
    case 'route-BUS':
    case 'route-TRAM':
    case 'route-RAIL':
    case 'route-SUBWAY':
    case 'route-FERRY':
    case 'route-FUNICULAR':
    case 'route-AIRPLANE':
      return !plain && suggestion.shortName ? [suggestion.gtfsId, suggestion.mode.toLowerCase(), suggestion.longName] : [suggestion.shortName, suggestion.longName, suggestion.agency ? suggestion.agency.name : undefined];
    case 'venue':
    case 'address':
    case 'street':
      return [suggestion.name, suggestion.label.replace(new RegExp(`${escapeRegExp(suggestion.name)}(,)?( )?`), '')];
    case 'favouriteStop':
    case 'stop':
      return plain ? [suggestion.name || suggestion.label || suggestion.address && suggestion.address.split(',')[0], getLocaladmin(suggestion)] : [suggestion.name, suggestion.id, getStopCode(suggestion), getLocaladmin(suggestion)];
    case 'favouriteStation':
    case 'station':
    default:
      return [suggestion.name || suggestion.label || suggestion.address && suggestion.address.split(',')[0], getLocaladmin(suggestion)];
  }
}, (item, plain) => {
  const i = cloneDeep(item);
  i.plain = plain;
  return i;
});

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".styles_button-shared__RSwxE, .styles_favourite-container__WTJm3 .styles_expandButton__mkemf, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 {\n  background-color: white;\n  display: flex;\n  flex-direction: row;\n  justify-content: center;\n  align-items: center;\n  border-radius: 5px;\n  border: 1px solid #ddd;\n  cursor: pointer;\n}\n.styles_button-shared__RSwxE:hover, .styles_favourite-container__WTJm3 .styles_expandButton__mkemf:hover, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1:hover {\n  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);\n}\n\n.styles_shimmer-shared__MGBAa, .styles_favourite-container__WTJm3 .styles_expandButton__mkemf > div, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 > div {\n  display: flex;\n  align-items: center;\n  overflow: hidden;\n}\n.styles_shimmer-shared__MGBAa .styles_shimmer__bIP9J, .styles_favourite-container__WTJm3 .styles_expandButton__mkemf > div .styles_shimmer__bIP9J, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 > div .styles_shimmer__bIP9J {\n  border-radius: 5px;\n}\n\n.styles_favourite-container__WTJm3 {\n  display: flex;\n  position: relative;\n  z-index: 1;\n  width: 100%;\n  height: 50px;\n  flex-shrink: 1;\n  flex-direction: row;\n  justify-content: space-between;\n  align-items: center;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 {\n  width: auto;\n  min-width: 0;\n  flex: 1;\n  flex-grow: 1;\n  height: 100%;\n  position: relative;\n  padding: 0;\n  margin-right: 8px;\n  overflow: hidden;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD {\n  display: flex;\n  width: 50px;\n  height: 100%;\n  justify-content: center;\n  align-items: center;\n  flex-shrink: 1;\n}\n@media screen and (min-width: 375px) {\n  .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD {\n    flex-shrink: 0;\n  }\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD svg {\n  width: 20px;\n  height: 20px;\n  position: relative;\n  left: 2px;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD.styles_place__4mHYj svg {\n  width: 24px;\n  height: 24px;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD.styles_home__msJXd svg, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD.styles_school__Hta8J svg, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD.styles_sport__YFDd0 svg, .styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD.styles_shopping__jMKed svg {\n  width: 18px;\n  height: 18px;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_icon__mAwUD.styles_work__uZX4R svg {\n  width: 20px;\n  height: 20px;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_favourite-location__RiyEV {\n  white-space: nowrap;\n  overflow: hidden;\n  text-align: start;\n  width: 100%;\n  flex-direction: row;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_favourite-location__RiyEV .styles_name__Fvsvr {\n  font-family: inherit;\n  font-size: 15px;\n  margin-right: 6px;\n  font-weight: var(--font-weight-medium);\n  font-stretch: normal;\n  font-style: normal;\n  letter-spacing: -0.6px;\n  color: #333;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_favourite-location__RiyEV .styles_name__Fvsvr:empty {\n  margin-top: 0;\n}\n.styles_favourite-container__WTJm3 .styles_favourite-content__6gqt1 .styles_favourite-location__RiyEV .styles_address__iBEhu {\n  font-family: inherit;\n  font-size: 12px;\n  margin-right: 6px;\n  font-weight: normal;\n  font-stretch: normal;\n  font-style: normal;\n  line-height: 1.67;\n  letter-spacing: -0.48px;\n  color: #666;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.styles_favourite-container__WTJm3 .styles_expandButton__mkemf {\n  height: 100%;\n  width: 42px;\n  flex-shrink: 0;\n  overflow: hidden;\n  padding: 0;\n}\n.styles_favourite-container__WTJm3 .styles_expandButton__mkemf > div {\n  justify-content: center;\n}\n.styles_favourite-container__WTJm3 .styles_expandButton__mkemf.styles_plus__ryt4n svg {\n  width: 16px;\n  height: 16px;\n}\n.styles_favourite-container__WTJm3 .styles_expandButton__mkemf.styles_arrow__EbmBZ svg {\n  width: 19px;\n  height: 19px;\n  transform: rotate(90deg);\n}\n.styles_favourite-container__WTJm3 .styles_expandButton__mkemf.styles_arrow__EbmBZ.styles_rotate__Nw2-A svg {\n  transform: rotate(-90deg);\n}\n\n.styles_favourite-suggestion-container__MVZBj {\n  position: relative;\n  display: flex;\n  justify-content: center;\n  padding-top: 9px;\n}\n.styles_favourite-suggestion-container__MVZBj .styles_divider__podkI {\n  display: block;\n  height: 6px;\n  background-color: #f2f5f7;\n  border-top: 1px solid rgba(0, 0, 0, 0.1);\n}\n.styles_favourite-suggestion-container__MVZBj .styles_favourite-suggestion-list__boIJY {\n  max-height: 30vh;\n  list-style-type: none;\n  padding: 0;\n  position: absolute;\n  z-index: 20;\n  width: 100%;\n  margin: 0 auto;\n  border-radius: 5px;\n  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.16);\n  border: solid 1px rgba(0, 0, 0, 0.1);\n  background-color: #fff;\n  overflow-x: hidden;\n  overflow-y: scroll;\n}\n.styles_favourite-suggestion-container__MVZBj .styles_favourite-suggestion-item__ptExh.styles_custom__87c-n {\n  font-size: 15px;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: -0.5;\n}\n.styles_favourite-suggestion-container__MVZBj .styles_favourite-suggestion-item__ptExh:hover {\n  background-color: #f2f5f7;\n}";
var styles = {"button-shared":"styles_button-shared__RSwxE","favourite-container":"styles_favourite-container__WTJm3","expandButton":"styles_expandButton__mkemf","favourite-content":"styles_favourite-content__6gqt1","shimmer-shared":"styles_shimmer-shared__MGBAa","shimmer":"styles_shimmer__bIP9J","icon":"styles_icon__mAwUD","place":"styles_place__4mHYj","home":"styles_home__msJXd","school":"styles_school__Hta8J","sport":"styles_sport__YFDd0","shopping":"styles_shopping__jMKed","work":"styles_work__uZX4R","favourite-location":"styles_favourite-location__RiyEV","name":"styles_name__Fvsvr","address":"styles_address__iBEhu","plus":"styles_plus__ryt4n","arrow":"styles_arrow__EbmBZ","rotate":"styles_rotate__Nw2-A","favourite-suggestion-container":"styles_favourite-suggestion-container__MVZBj","divider":"styles_divider__podkI","favourite-suggestion-list":"styles_favourite-suggestion-list__boIJY","favourite-suggestion-item":"styles_favourite-suggestion-item__ptExh","custom":"styles_custom__87c-n"};
styleInject(css_248z);

/* eslint sort-keys: "error" */
const translations = {
  de: {
    translation: {
      'add-favourite': 'Füge Favorit hinzu',
      'add-home': 'Setze Zuhause',
      'add-place': 'Setze Ort',
      'add-work': 'Setze Arbeitsort',
      edit: 'Bearbeiten',
      home: 'Zuhause',
      'open-favourites': 'Öffne Favoriten',
      work: 'Arbeit'
    }
  },
  en: {
    translation: {
      'add-destination': 'Set as destination',
      'add-favourite': 'Add favourite',
      'add-home': 'Add home',
      'add-place': 'Add place',
      'add-work': 'Add work',
      edit: 'Edit',
      'favourites-list': 'Favourites list',
      home: 'Home',
      'open-favourites': 'Open favourites',
      work: 'Work'
    }
  },
  fi: {
    translation: {
      'add-destination': 'Aseta määränpääksi',
      'add-favourite': 'Lisää suosikki',
      'add-home': 'Lisää koti',
      'add-place': 'Lisää paikka',
      'add-work': 'Lisää työ',
      edit: 'Muokkaa',
      'favourites-list': 'Suosikit lista',
      home: 'Koti',
      'open-favourites': 'Avaa suosikit',
      work: 'Työ'
    }
  },
  ga: {
    translation: {
      'add-destination': 'Socraigh mar cheann scríbe',
      'add-favourite': 'Cuir ceanán leis',
      'add-home': 'Cuir baile leis',
      'add-place': 'Cuir áit leis',
      'add-work': 'Cuir obair leis',
      edit: 'Cuir in eagar',
      'favourites-list': 'Liosta ceanán',
      home: 'Baile',
      'open-favourites': 'Oscail ceanáin',
      work: 'Obair'
    }
  },
  pl: {
    translation: {
      'add-destination': 'Wybierz jako punkt docelowy',
      'add-favourite': 'Dodaj ulubione',
      'add-home': 'Dodaj dom',
      'add-place': 'Dodaj miejsce',
      'add-work': 'Dodaj pracę',
      edit: 'Edytuj',
      'favourites-list': 'Lista ulubionych',
      home: 'Dom',
      'open-favourites': 'Pokaż ulubione',
      work: 'Praca'
    }
  },
  sv: {
    translation: {
      'add-destination': 'Ställa in som destination',
      'add-favourite': 'Lägg till favorit',
      'add-home': 'Lägg till hem',
      'add-place': 'Lägg till plats',
      'add-work': 'Lägg till arbete',
      edit: 'Redigera',
      'favourites-list': 'Favoritlista',
      home: 'Hem',
      'open-favourites': 'Öppna favoriter',
      work: 'Arbete'
    }
  }
};

const i18n = createInstance({
  fallbackLng: 'en',
  // debug: true,
  resources: translations,
  defaultNS: 'translation',
  react: {
    useSuspense: true
  }
});
i18n.init();

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const isKeyboardSelectionEvent = event => {
  const space = [13, ' ', 'Spacebar'];
  const enter = [32, 'Enter'];
  const key = event && (event.key || event.which || event.keyCode) || '';
  if (!key || !space.concat(enter).includes(key)) {
    return false;
  }
  event.preventDefault();
  return true;
};

/**
 * Utility to format location [name, address] pair.
 * @param {LocationProperties} favourite
 * @returns {Array.<string>}
 */
const formatFavourite = favourite => favourite && (favourite.name || favourite.address) ? formatFavouritePlaceLabel(favourite.name, favourite.address) : [];
const FavouriteLocation = ({
  clickItem,
  iconId,
  text,
  label,
  isLoading,
  color,
  lang
}) => {
  const [t] = useTranslation();
  const ariaLabel = label === '' ? text : `${text} ${label} ${t('add-destination', {
    lng: lang
  })}`;
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    tabIndex: "0",
    className: cx(styles['favourite-content']),
    onClick: clickItem,
    onKeyDown: e => isKeyboardSelectionEvent(e) && clickItem(),
    "aria-label": ariaLabel
  }, /*#__PURE__*/React.createElement(Shimmer, {
    active: isLoading,
    className: styles.shimmer
  }, /*#__PURE__*/React.createElement("span", {
    className: cx(styles.icon, styles[iconId])
  }, /*#__PURE__*/React.createElement(Icon, {
    img: iconId,
    color: color
  })), /*#__PURE__*/React.createElement("div", {
    className: styles['favourite-location']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.name
  }, text), /*#__PURE__*/React.createElement("div", {
    className: styles.address
  }, label))));
};
process.env.NODE_ENV !== "production" ? FavouriteLocation.propTypes = {
  clickItem: PropTypes.func.isRequired,
  iconId: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  color: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired
} : void 0;
FavouriteLocation.defaultProps = {
  isLoading: false
};

/**
 * FavouriteBar renders favourites. FavouriteBar displays the first two favourites, the rest are shown in a list.
 * @example
 * <FavouriteBar
 *   favourites={favourites}
 *   onClickFavourite={onClickFavourite}
 *   onAddPlace={this.addPlace}
 *   onAddHome={this.addHome}
 *   onAddWork={this.addWork}
 *   lang={this.props.lang}
 *   isLoading={this.props.isLoading}
 * />
 */
class FavouriteBar extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "toggleList", () => {
      const eventDiff = new Date().getTime() - this.state.timestamp;
      if (eventDiff > 200) {
        this.setState(prevState => ({
          listOpen: !prevState.listOpen,
          timestamp: new Date().getTime()
        }), () => {
          if (this.state.listOpen) {
            this.firstItemRef.current?.focus();
          } else {
            this.expandListRef.current?.focus();
          }
        });
      }
    });
    _defineProperty(this, "translate", id => {
      return this.props.t(id, {
        lng: this.props.lang
      });
    });
    _defineProperty(this, "handleClickOutside", event => {
      if (this.suggestionListRef.current && this.expandListRef.current && !this.suggestionListRef.current.contains(event.target) && !this.expandListRef.current.contains(event.target)) {
        this.setState({
          listOpen: false
        });
      }
    });
    _defineProperty(this, "suggestionSelected", index => {
      const {
        favourites
      } = this.state;
      if (index < favourites.length) {
        this.props.onClickFavourite(favourites[index]);
      } else if (index === favourites.length) {
        this.props.onAddPlace();
      } else if (index === favourites.length + 1) {
        this.props.onEdit();
      }
      this.toggleList();
    });
    _defineProperty(this, "handleKeyDown", (event, index) => {
      const {
        listOpen,
        favourites
      } = this.state;
      const key = event && (event.key || event.which || event.keyCode) || '';
      if (isKeyboardSelectionEvent(event)) {
        if (!listOpen) {
          this.toggleList();
        } else {
          this.suggestionSelected(index);
        }
      } else if (key === 'Escape' || key === 27) {
        if (listOpen) {
          this.toggleList();
        }
      } else if (key === 'Tab' && !event.shiftKey && index === favourites.length + this.getCustomSuggestions().length - 1) {
        this.setState({
          listOpen: false
        });
      }
    });
    _defineProperty(this, "renderSuggestion", (item, index, ariaLabelSuffix = '') => {
      const id = `favourite-suggestion-list--item-${index}`;
      return /*#__PURE__*/React.createElement("li", {
        key: `favourite-suggestion-item-${index}`
      }, /*#__PURE__*/React.createElement("div", {
        role: "button",
        type: "button",
        tabIndex: "0",
        id: id,
        className: cx(styles['favourite-suggestion-item']),
        onClick: () => this.suggestionSelected(index),
        onKeyDown: e => this.handleKeyDown(e, index),
        ref: index === 0 ? this.firstItemRef : ''
        // eslint-disable-next-line jsx-a11y/no-autofocus
        ,
        autoFocus: index === 0,
        "aria-label": `${item?.name || ''} ${item?.address || ''} ${ariaLabelSuffix}`
      }, /*#__PURE__*/React.createElement(SuggestionItem, {
        item: item,
        colors: this.props.colors,
        fontWeights: this.props.fontWeights
      })));
    });
    _defineProperty(this, "getCustomSuggestions", () => {
      const customSuggestions = [{
        name: this.translate('add-place'),
        selectedIconId: 'favourite'
      }];
      if (this.props.favourites.length === 0) {
        return customSuggestions;
      }
      return [...customSuggestions, {
        name: this.translate('edit'),
        selectedIconId: 'edit'
      }];
    });
    this.state = {
      listOpen: false,
      firstFavourite: props.favourites[0] || null,
      secondFavourite: props.favourites[1] || null,
      favourites: props.favourites.slice(2, props.favourites.length),
      timestamp: 0
    };
    this.expandListRef = /*#__PURE__*/React.createRef();
    this.suggestionListRef = /*#__PURE__*/React.createRef();
    this.firstItemRef = /*#__PURE__*/React.createRef();
  }
  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      favourites
    } = prevState;
    const nextFavourites = nextProps.favourites;
    if (!isEmpty(differenceWith(nextFavourites, favourites, isEqual)) || !isEmpty(differenceWith(favourites, nextFavourites, isEqual)) || !isEqual(nextFavourites, favourites) || isEmpty(nextFavourites)) {
      return {
        firstFavourite: nextFavourites[0] || null,
        secondFavourite: nextFavourites[1] || null,
        favourites: nextFavourites.slice(2, nextFavourites.length)
      };
    }
    return null;
  }
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }
  render() {
    const {
      onClickFavourite,
      isLoading,
      fontWeights
    } = this.props;
    const {
      primary
    } = this.props.colors;
    const {
      listOpen,
      favourites,
      firstFavourite,
      secondFavourite
    } = this.state;
    const expandIcon = this.props.favourites.length === 0 ? 'plus' : 'arrow';
    const [name1, address1] = formatFavourite(firstFavourite);
    const [name2, address2] = formatFavourite(secondFavourite);
    return /*#__PURE__*/React.createElement("div", {
      style: {
        '--font-weight-medium': fontWeights.medium
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['favourite-container']
    }, /*#__PURE__*/React.createElement(FavouriteLocation, {
      text: name1 || this.translate('add-home'),
      label: address1 || '',
      clickItem: () => firstFavourite ? onClickFavourite(firstFavourite) : this.props.onAddHome(),
      iconId: firstFavourite && firstFavourite.selectedIconId ? FavouriteBar.FavouriteIconIdToNameMap[firstFavourite.selectedIconId] : 'home',
      isLoading: isLoading,
      color: primary,
      lang: this.props.lang
    }), /*#__PURE__*/React.createElement(FavouriteLocation, {
      text: name2 || this.translate('add-work'),
      label: address2 || '',
      clickItem: () => secondFavourite ? onClickFavourite(secondFavourite) : this.props.onAddWork(),
      iconId: secondFavourite && secondFavourite.selectedIconId ? FavouriteBar.FavouriteIconIdToNameMap[secondFavourite.selectedIconId] : 'work',
      isLoading: isLoading,
      color: primary,
      lang: this.props.lang
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: cx(styles.expandButton, styles[expandIcon], listOpen && styles.rotate),
      ref: this.expandListRef,
      id: "favourite-expand-button",
      onKeyDown: e => this.handleKeyDown(e),
      onClick: () => this.toggleList(),
      "aria-expanded": this.state.listOpen,
      "aria-label": this.translate('open-favourites')
    }, /*#__PURE__*/React.createElement(Shimmer, {
      active: isLoading
    }, /*#__PURE__*/React.createElement(Icon, {
      img: expandIcon,
      color: primary
    })))), /*#__PURE__*/React.createElement("div", {
      className: styles['favourite-suggestion-container']
    }, listOpen && /*#__PURE__*/React.createElement("ul", {
      className: styles['favourite-suggestion-list'],
      id: "favourite-suggestion-list",
      ref: this.suggestionListRef,
      "aria-label": this.translate('favourites-list')
    }, favourites.map((item, index) => {
      const favouriteLabel = formatFavouritePlaceLabel(item.name, item.address);
      const address = favouriteLabel[1];
      return this.renderSuggestion(_objectSpread(_objectSpread({}, item), {}, {
        address
      }), index, `, ${this.translate('add-destination')}`);
    }), favourites.length > 0 && /*#__PURE__*/React.createElement("div", {
      "aria-hidden": true,
      className: styles.divider
    }), this.getCustomSuggestions().map((item, index) => this.renderSuggestion(item, favourites.length + index, undefined)))));
  }
}
_defineProperty(FavouriteBar, "defaultProps", {
  onClickFavourite: () => ({}),
  onAddPlace: () => ({}),
  onEdit: () => ({}),
  onAddHome: () => ({}),
  onAddWork: () => ({}),
  lang: 'fi',
  isLoading: false,
  colors: defaultColors,
  fontWeights: {
    medium: 500
  }
});
_defineProperty(FavouriteBar, "FavouriteIconIdToNameMap", {
  'icon-icon_place': 'place',
  'icon-icon_home': 'home',
  'icon-icon_work': 'work',
  'icon-icon_sport': 'sport',
  'icon-icon_school': 'school',
  'icon-icon_shopping': 'shopping',
  // Map two buggy ids temporarily. This code can be removed at some point.
  icon_home: 'home',
  icon_work: 'work'
});
process.env.NODE_ENV !== "production" ? FavouriteBar.propTypes = {
  /** Required. Array of favourites, favourite object contains following properties.
   * @type {Array<object>}
   * @property {string} address
   * @property {string} gtfsId
   * @property {string} gid
   * @property {number} lat
   * @property {number} lon
   * @property {string} name
   * @property {string} selectedIconId
   * @property {string} favouriteId
   */
  favourites: PropTypes.arrayOf(PropTypes.shape({
    address: PropTypes.string,
    gtfsId: PropTypes.string,
    gid: PropTypes.string,
    lat: PropTypes.number,
    name: PropTypes.string,
    lon: PropTypes.number,
    selectedIconId: PropTypes.string,
    favouriteId: PropTypes.string
  })).isRequired,
  /** Optional. Function for clicking favourites. */
  onClickFavourite: PropTypes.func,
  /** Optional. Function for selecting "Add place" from suggestions. */
  onAddPlace: PropTypes.func,
  /** Optional. Function for selected "Edit" from suggestions. */
  onEdit: PropTypes.func,
  /** Optional. Function for "Add home" button. */
  onAddHome: PropTypes.func,
  /** Optional. Function for "Add work" button. */
  onAddWork: PropTypes.func,
  /** Optional. Language, fi, en or sv. */
  lang: PropTypes.string,
  /** Optional. Whether to show loading animation, true or false. */
  isLoading: PropTypes.bool,
  /** Optional color palette */
  colors: PropTypes.objectOf(PropTypes.string),
  /** Optional. */
  fontWeights: PropTypes.shape({
    /** Default value is 500. */
    medium: PropTypes.number
  }),
  /** Translation function */
  t: PropTypes.func.isRequired
} : void 0;
const FavouriteBarWithTranslation = withTranslation()(FavouriteBar);
var index = props => /*#__PURE__*/React.createElement(I18nextProvider, {
  i18n: i18n
}, /*#__PURE__*/React.createElement(FavouriteBarWithTranslation, props));

export { index as default };
//# sourceMappingURL=index.js.map
